<template>
  <div :class="comment.parent_comment_id ? 'ml-auto reply' : ''">
    <div class="d-flex">
      <router-link :to="posterProfileLink" class="mr-2 img-link">
        <img v-image class="comment-poster-img clickable" :src="resizeUpload(getProfilePicture(comment), '80x80')" />
      </router-link>

      <div v-if="!isEditing" class="card mb-3 ml-2 comment-text bg-light">
        <div @mouseover="hover = true" @mouseleave="hover = false">
          <div class="card-body d-flex p-3">
            <div class="w-100">
              <div class="d-flex flex-wrap">
                <router-link :to="posterProfileLink">
                  <strong class="clickable text-black"
                    >{{
                      comment.commented_as.type === commentedAsTypes.USER
                        ? comment.commented_as.username
                        : get(comment.commented_as, 'info.name', 'Unknown')
                    }}
                  </strong>
                </router-link>

                <small class="ml-auto">{{ formatDescDateTime(comment.created) }}</small>
              </div>
              <div class="mt-2 px-0">
                <span v-safe-html="comment.text.replace(/(?:\r\n|\r|\n)/g, '<br>')" />
              </div>

              <div v-if="isAuthenticated" class="mt-2 d-flex justify-content-between">
                <div class="d-flex justify-content-start">
                  <sub v-if="comment.is_edited" class="text-muted text-sm py-3"><i>(Edited)</i></sub>
                </div>
                <div class="mt-2 d-flex justify-content-end">
                  <a v-if="isuser" href="#" class="d-flex align-items-center text-danger" @click.prevent="Delete">
                    <i color="danger" class="ti-trash mr-1" />
                    <span v-if="!isMobSmallScreen">Delete</span></a
                  >

                  <a v-if="isuser" href="#" class="ml-2 d-flex align-items-center text-secondary" @click.prevent="edit">
                    <i color="secondary" class="ti-pencil-alt mr-1" /><span v-if="!isMobSmallScreen">Edit</span>
                  </a>

                  <a href="#" class="ml-2 d-flex align-items-center" @click.prevent="onReply">
                    <i class="ti-comment mr-1" /><span v-if="!isMobSmallScreen">Reply</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="w-100 ml-2 mb-2">
        <PostComment
          :target="target"
          :commenting-on-type="commentingOnType"
          :is-edit="true"
          :comment-id="comment.id"
          :existing-text="comment.text"
          @posted="onCommentUpdate"
          @canceled="onUpdateCancel"
        />
      </div>
    </div>

    <div v-if="isReplying" class="mb-2">
      <PostComment
        :target="target"
        :commenting-on-type="commentingOnType"
        :is-parent="!!comment.parent_comment_id"
        :parent-comment-id="comment.parent_comment_id || comment.id"
        :reply-to-mention-name="replyToMentionName"
        :reply-to-comment="comment.id"
        @posted="onReplyPosted"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import { alertController } from '@ionic/vue';
import { formatDescDateTime } from '@/utils/dateTime';
import constants from '@/statics/constants';
import PostComment from '@/shared-components/PostComment.vue';

import { Character, User } from '@/types/static-types';
import namespace from '@/store/namespace';
import { toast } from '@/native';
import { deleteComment } from '@/actions/comments';
import { authStore } from '~/pinia-store/auth';
import { useWindowSize } from '~/composition/useWindowSize';

@Options({
  name: 'CommentsListItem',
  components: { PostComment },
})
export default class CommentsListItem extends Vue {
  @Prop() comment!: any;
  @Prop() target!: Character | User;
  @Prop({ required: true }) commentingOnType!: string; // character, user

  resizeUpload = resizeUpload;
  public get isAuthenticated() {
    const { isAuthenticated } = authStore();
    return isAuthenticated.value;
  }

  public get windowWidth() {
    const { width } = useWindowSize();
    return width.value;
  }

  @Action('postComment', { namespace: namespace.CharactersModule })
  public postComment!: Function;

  public get user() {
    const { user } = authStore();
    return user.value;
  }

  hover = false;
  isReplying = false;
  replyToMentionName: string | null = null;
  isEditing = false;
  isdeleting = false;
  isSaving = false;

  formatDescDateTime = formatDescDateTime;
  get = get;
  commentedAsTypes = constants.commentedAsTypes;

  public onReply() {
    this.replyToMentionName =
      this.comment.commented_as.type === this.commentedAsTypes.USER
        ? this.comment.commented_as.username
        : get(this.comment.commented_as, 'info.name', 'Unknown');
    this.isReplying = !this.isReplying;
  }

  public get isuser() {
    try {
      return this.comment.commenter_user_id === this.user.id;
    } catch (error) {
      return false;
    }
  }

  public getProfilePicture(comment: any) {
    if (comment.commented_as.type === this.commentedAsTypes.CHARACTER) {
      return get(comment.commented_as, 'info.cropProfilePicture', '/empty.png');
    } else if (comment.commented_as.type === this.commentedAsTypes.USER) {
      return comment.commented_as.profile_picture_url;
    }
    return '/images/user.jpg';
  }

  public async Delete() {
    if (this.isdeleting) return;

    this.isdeleting = true;

    try {
      const alert = await alertController.create({
        cssClass: 'custom-alert ',
        message: `Are you sure you want to delete this comment?`,
        backdropDismiss: false,
        translucent: false,
        buttons: [
          {
            text: 'Cancel',
          },
          {
            text: 'Confirm',
            role: 'confirm',
            cssClass: 'text-danger',
          },
        ],
      });
      await alert.present();
      const { role } = await alert.onDidDismiss();

      if (role === 'confirm') {
        deleteComment(this.comment.id);

        setTimeout(() => {
          this.$emit('commentDeleted', { commentId: this.comment.id, parentId: this.comment.parent_comment_id });
        }, 200);

        toast.show('Comment deleted successfully', 'nonative', 'success');
      }
    } catch (err) {
      toast.show('Some error has occurred. Please try again later.', 'nonative', 'danger');
    }

    this.isdeleting = false;
  }

  public edit() {
    this.isEditing = true;
  }

  public onCommentUpdate() {
    this.isEditing = false;
    this.comment.is_edited = true;
    this.$nextTick(() => {
      this.$emit('posted');
    });
  }

  public onUpdateCancel() {
    this.isEditing = false;
  }

  public get isMobSmallScreen() {
    return this.windowWidth <= 420;
  }

  public onReplyPosted() {
    this.isReplying = false;
    this.replyToMentionName = null;
    this.$nextTick(() => {
      this.$emit('posted');
    });
  }

  public get posterProfileLink() {
    return this.comment.commented_as.type === this.commentedAsTypes.USER
      ? { name: 'profile', params: { username: this.comment.commented_as.username } }
      : { name: 'character-profile-new', params: { slug: this.comment.commented_as.slug } };
  }
}
</script>

<style lang="sass" scoped>
.comment-poster-img
  width: 60px
  height: 60px
  border-radius: 40px
  border: solid gray 0.1px
  object-fit: cover
.clickable
  cursor: pointer
.clickable:hover
  opacity: 0.7

.comment-text
  width: calc(100% - 70px)

.reply
  width: 75% !important
  @media(max-width: 500px)
    width: 90% !important
</style>
